import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Calculator, CheckCircle, Clock, Percent, 
  ChevronDown, ArrowRight, Shield, FileText, Users,
  DollarSign, Calendar, TrendingUp, Zap, Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const FinanciamientoPropiaPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const plans = [
    {
      name: 'Plan Inicial',
      months: 6,
      downPayment: '30%',
      interestRate: '0%',
      highlight: false,
      features: ['Sin intereses', 'Cuotas fijas', 'Aprobación inmediata'],
      ideal: 'Ideal para sistemas pequeños'
    },
    {
      name: 'Plan Estándar',
      months: 12,
      downPayment: '20%',
      interestRate: '0%',
      highlight: true,
      features: ['Sin intereses', 'Cuotas accesibles', 'Mayor plazo', 'El más elegido'],
      ideal: 'Ideal para sistemas residenciales'
    },
    {
      name: 'Plan Extendido',
      months: 18,
      downPayment: '15%',
      interestRate: '5%',
      highlight: false,
      features: ['Cuota mínima', 'Máximo plazo', 'Flexible'],
      ideal: 'Ideal para proyectos grandes'
    },
    {
      name: 'Plan Premium',
      months: 24,
      downPayment: '10%',
      interestRate: '8%',
      highlight: false,
      features: ['Mínima entrada', 'Cuotas fijas', 'Mayor accesibilidad'],
      ideal: 'Ideal para comercios e industrias'
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Proceso Simplificado',
      description: 'Financiamos directamente con un proceso ágil y sin complicaciones innecesarias.'
    },
    {
      icon: Clock,
      title: 'Aprobación Rápida',
      description: 'Evaluamos tu solicitud de forma ágil para darte una respuesta lo antes posible.'
    },
    {
      icon: Shield,
      title: 'Sin Garantías Extra',
      description: 'El propio sistema solar sirve como garantía, sin necesidad de hipotecas ni avales.'
    },
    {
      icon: Calendar,
      title: 'Cuotas Flexibles',
      description: 'Adaptamos el plan de pagos a tu flujo de caja y capacidad de pago mensual.'
    },
    {
      icon: TrendingUp,
      title: 'Ahorro desde el Día 1',
      description: 'Comenzás a ahorrar en electricidad desde el primer mes de instalación.'
    },
    {
      icon: Users,
      title: 'Atención Personalizada',
      description: 'Analizamos cada caso de forma individual para ofrecerte la mejor solución.'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Solicitud',
      description: 'Completá el formulario de solicitud con tus datos básicos y el sistema que querés financiar.',
      icon: FileText
    },
    {
      step: 2,
      title: 'Evaluación',
      description: 'Analizamos tu solicitud y te enviamos una propuesta personalizada en 24 horas.',
      icon: Calculator
    },
    {
      step: 3,
      title: 'Aprobación',
      description: 'Una vez aprobado, firmamos el contrato y programamos la instalación.',
      icon: CheckCircle
    },
    {
      step: 4,
      title: 'Instalación',
      description: 'Instalamos tu sistema solar y comenzás a ahorrar mientras pagás cómodamente.',
      icon: Zap
    }
  ];

  const faqs = [
    {
      question: '¿Qué documentos necesito para solicitar financiamiento?',
      answer: 'Solo necesitás cédula de identidad, comprobante de ingresos (recibo de sueldo o declaración de IVA para comercios) y una factura de electricidad reciente. No pedimos garantías adicionales.'
    },
    {
      question: '¿Cuánto tiempo demora la aprobación?',
      answer: 'La evaluación de tu solicitud demora menos de 24 horas hábiles. Una vez aprobada, podemos programar la instalación para la semana siguiente.'
    },
    {
      question: '¿Puedo adelantar cuotas o cancelar anticipadamente?',
      answer: '¡Sí! No cobramos penalidad por pago anticipado. Podés adelantar cuotas cuando quieras y se recalcula el interés a tu favor.'
    },
    {
      question: '¿Qué pasa si no puedo pagar una cuota?',
      answer: 'Entendemos que pueden surgir imprevistos. Contactanos antes del vencimiento y buscaremos una solución flexible como reprogramar el pago o ajustar el plan.'
    },
    {
      question: '¿El financiamiento incluye la instalación?',
      answer: 'Sí, el monto financiado incluye todo: paneles, inversor, estructura, instalación, conexión a ANDE y garantías. No hay costos ocultos.'
    },
    {
      question: '¿Puedo financiar solo una parte del sistema?',
      answer: 'Sí, podés dar una entrada mayor y financiar solo el monto restante, reduciendo así el número de cuotas o el valor de cada una.'
    }
  ];

  const requirements = [
    'Cédula de identidad vigente',
    'Comprobante de ingresos (recibo de sueldo o IVA)',
    'Factura de electricidad reciente',
    'Ser propietario o tener autorización del propietario',
    'Antigüedad laboral mínima de 6 meses (empleados)',
    'Negocio activo mínimo de 1 año (comercios)'
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="financiamiento-propia-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-32 bg-gradient-to-br from-zinc-900 via-zinc-800 to-emerald-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <CreditCard className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Financiamiento Directo</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Financiación Propia
            <span className="block text-emerald-400">Sin Complicaciones</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl mb-8">
            Financiamos tu sistema solar directamente. Sin largas esperas, 
            sin requisitos excesivos. Aprobación rápida y proceso simple.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg shadow-emerald-500/30"
            >
              Calcular Mi Sistema
              <ArrowRight className="w-5 h-5" />
            </button>
            <a 
              href="https://wa.me/595991344956?text=Hola,%20me%20interesa%20el%20financiamiento%20propio%20de%20Zenex"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
            >
              <Phone className="w-5 h-5" />
              Consultar Ahora
            </a>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mt-16 max-w-xl">
            {[
              { value: 'Rápida', label: 'Aprobación' },
              { value: 'Flexible', label: 'Planes adaptados a vos' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                <p className="text-3xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-sm text-zinc-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Section - Oculta temporalmente
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Elegí Tu Plan de Financiamiento
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Adaptamos el financiamiento a tus necesidades. Todos los planes incluyen instalación completa.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative p-6 rounded-2xl transition-all duration-300 cursor-pointer ${
                  plan.highlight 
                    ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/30 scale-105' 
                    : 'bg-white border-2 border-zinc-200 hover:border-emerald-500 hover:shadow-lg'
                } ${selectedPlan === index ? 'ring-4 ring-emerald-400' : ''}`}
                onClick={() => setSelectedPlan(index)}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                    MÁS POPULAR
                  </div>
                )}
                
                <h3 className={`text-xl font-bold mb-2 ${plan.highlight ? 'text-white' : 'text-zinc-900'}`}>
                  {plan.name}
                </h3>
                
                <div className={`text-4xl font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-emerald-600'}`}>
                  {plan.months}
                  <span className="text-lg font-normal"> cuotas</span>
                </div>
                
                <p className={`text-sm mb-4 ${plan.highlight ? 'text-emerald-100' : 'text-zinc-500'}`}>
                  {plan.ideal}
                </p>
                
                <div className={`space-y-2 mb-6 ${plan.highlight ? 'text-emerald-100' : 'text-zinc-600'}`}>
                  <div className="flex justify-between">
                    <span>Entrada:</span>
                    <span className="font-semibold">{plan.downPayment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Interés:</span>
                    <span className="font-semibold">{plan.interestRate}</span>
                  </div>
                </div>
                
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className={`w-4 h-4 ${plan.highlight ? 'text-emerald-300' : 'text-emerald-500'}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button 
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    plan.highlight 
                      ? 'bg-white text-emerald-600 hover:bg-emerald-50' 
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  Elegir Plan
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Ventajas de Nuestro Financiamiento
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Diseñamos un sistema de financiamiento pensado para hacer accesible la energía solar a todos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="p-6 bg-zinc-50 rounded-xl hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                    <Icon className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-zinc-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Proceso Simple en 4 Pasos
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Desde la solicitud hasta tener tu sistema funcionando en tiempo récord.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative text-center">
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-emerald-600/30"></div>
                  )}
                  <div className="relative z-10 w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <div className="text-emerald-400 font-bold mb-2">Paso {step.step}</div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-zinc-400 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                Requisitos Mínimos
              </h2>
              <p className="text-lg text-zinc-600 mb-8">
                Nuestro proceso de aprobación es simple y rápido. Solo necesitás cumplir con estos requisitos básicos.
              </p>
              <ul className="space-y-4">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-zinc-900 mb-6 flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Simulador Rápido
              </h3>
              <p className="text-zinc-600 mb-6">
                Usá nuestra calculadora solar para obtener un presupuesto personalizado con opciones de financiamiento.
              </p>
              <button 
                onClick={() => navigate('/calculadora')}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                Calcular Mi Sistema
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-zinc-600">
              Todo lo que necesitás saber sobre nuestro financiamiento propio.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para empezar a ahorrar?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Consultanos sin compromiso y te asesoramos sobre la mejor opción de financiamiento para vos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20quiero%20información%20sobre%20financiamiento%20propio"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Contactar por WhatsApp
            </a>
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-emerald-800 text-white font-semibold rounded-xl hover:bg-emerald-900 transition-all flex items-center gap-2"
            >
              <Calculator className="w-5 h-5" />
              Calcular Sistema
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default FinanciamientoPropiaPage;
